import { collection, doc, getDocs, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { db, ensureUser, idToken } from '../firebase';

const BACKEND = ((import.meta.env.VITE_BACKEND_URL as string | undefined) || '').replace(/\/$/, '');
async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await idToken();
  const res = await fetch(`${BACKEND}${path}`, { ...init, headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json', ...(init.headers || {}) } });
  if (!res.ok) { const body = await res.json().catch(() => ({})); throw Object.assign(new Error(body.error || `${res.status}`), { code: body.code, status: res.status }); }
  return res.json();
}

export interface Me { name: string | null; crew: 'on' | 'off' | 'unasked'; credits: number | null; gifted: number; fakeDoorUsed: boolean; madeDays: number }
export const getMe = () => call<Me>('/api/kindling/me');
export const setCrew = (crew: 'on' | 'off') => call<{ crew: string }>('/api/kindling/crew', { method: 'POST', body: JSON.stringify({ crew }) });

let answering: Promise<void> = Promise.resolve();
/**
 * The permission screen's answer, sent without making anyone wait for it:
 * the screen moves on at once (interview 5: "Let them" sat 27 s on a cold
 * start). One retry, then it is left to the profile copy. Anything the server
 * must see *after* the answer (the told story, which is developed only when the
 * crew are on) waits on `crewAnswered()`, so the two never cross in flight.
 */
export function answerCrew(crew: 'on' | 'off'): Promise<void> {
  answering = setCrew(crew)
    .catch(() => new Promise((r) => setTimeout(r, 2000)).then(() => setCrew(crew)))
    .then(() => undefined, () => undefined);
  return answering;
}
export const crewAnswered = (): Promise<void> => answering;
export const renderInspiration = (inspirationId: string) => call<{ characterId: string; imageUrl: string; credits: number }>('/api/kindling/renders', { method: 'POST', body: JSON.stringify({ inspirationId }) });
export const nameCharacter = (id: string, name: string) => call<{ ok: boolean }>(`/api/kindling/characters/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ name }) });
export const creditsWanted = () => call<{ granted: boolean; credits: number }>('/api/kindling/credits/wanted', { method: 'POST', body: '{}' });
export type FilmSlot = 'someone' | 'somewhere' | 'a want' | 'a look' | 'a line';
export interface FilmStatus {
  rolls: number; freeRolls: number; missing: FilmSlot[];
  film: { postId: string; title: string; status: 'pending' | 'done' | 'failed'; videoUrl?: string; posterUrl?: string; error?: string; rolledAt?: number; rolledKey?: string } | null;
  /** the crew's poster of a row, and the key of the row as it stands (they match when the poster is current) */
  poster: { key: string; url: string; at: number } | null;
  pieceKey: string;
}
/** Where the first film stands: pieces missing, rolls had, the newest clip (kindling-film.service). */
export const getFilm = () => call<FilmStatus>('/api/kindling/film');
/** Roll it. Throws with code not_ready / roll_paid / out_of_ink / crew_off. */
export const rollFilm = () => call<{ postId: string; title: string; status: 'pending' }>('/api/kindling/film/roll', { method: 'POST', body: '{}' });
/** The fake door on a second roll: counted, never granted. */
export const drawPoster = (picks?: Record<string, unknown>) => call<{ poster: { key: string; url: string; at: number }; cached: boolean }>('/api/kindling/film/poster', { method: 'POST', body: JSON.stringify({ picks }) });
export type LineSlot = Exclude<FilmSlot, 'a line'>;
export type LinePart = { text: string; slot?: LineSlot };
/** The row's words said as one sentence by the crew; `parts: null` means show them plainly. Throws crew_off. */
export const sayFilmLine = (pieces: Partial<Record<LineSlot, string>>) => call<{ parts: LinePart[] | null; cached: boolean }>('/api/kindling/film/line', { method: 'POST', body: JSON.stringify({ pieces }) });
export const filmWanted = () => call<{ wanted: number }>('/api/kindling/film/wanted', { method: 'POST', body: '{}' });
/** The crew's quick sketch of an idea roll (backend kindling-sketch.service). Throws with code crew_off / sketch_limit / blocked / out_of_ink. */
export const sketchIdea = (scene: string, dream?: string) => call<{ id: string; url: string }>('/api/kindling/sketch', { method: 'POST', body: JSON.stringify({ scene, ...(dream ? { dream } : {}) }) });
export const comebackGift = () => call<{ granted: boolean; credits: number }>('/api/kindling/credits/comeback', { method: 'POST', body: '{}' });

/** Everything you made, as a file. */
export async function downloadExport(): Promise<void> {
  const token = await idToken();
  const res = await fetch(`${BACKEND}/api/kindling/export`, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) throw new Error(`export failed: ${res.status}`);
  const blob = await res.blob();
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `kindling-${new Date().toISOString().slice(0, 10)}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
}

export type ItemKind = 'character' | 'place' | 'premise' | 'style' | 'line';
export interface Inspiration { id: string; kind: ItemKind; text: string; quote: string; fromPostId: string; madeFor: number; status: 'new' | 'kept' | 'discarded'; slot: string; renderedAs?: string }
export interface Character { id: string; kind: ItemKind; name: string | null; text: string; /** the person's exact words the drawing came from */ quote?: string; imageUrl: string; fromPostId: string; createdAt?: number; /** "me": the drawing "Who are you?" made of you (backend keepMe) */ self?: boolean }

export async function listInspirations(): Promise<Inspiration[]> {
  const user = await ensureUser();
  const snap = await getDocs(collection(db, 'kindling-inspirations', user.uid, 'items'));
  return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Inspiration, 'id'>) })).filter((i) => i.status !== 'discarded');
}

/**
 * The app has shown what the crew found, so the hourly cue must not email
 * about it. Written here rather than by the backend because this is the only
 * place that knows the person actually saw it.
 */
export async function markFindsSeen(): Promise<void> {
  const user = await ensureUser();
  await setDoc(doc(db, 'kindling-users', user.uid), { found: { seenAt: serverTimestamp() } }, { merge: true });
}

export async function setInspirationStatus(id: string, status: 'kept' | 'discarded'): Promise<void> {
  const user = await ensureUser();
  await updateDoc(doc(db, 'kindling-inspirations', user.uid, 'items', id), { status });
}

export async function listCharacters(): Promise<Character[]> {
  const user = await ensureUser();
  const snap = await getDocs(collection(db, 'kindling-characters', user.uid, 'items'));
  return snap.docs.map((d) => { const x = d.data(); const ts = x.createdAt as { toMillis?: () => number } | undefined; return { id: d.id, kind: x.kind, name: x.name ?? null, text: x.text, quote: x.quote || undefined, imageUrl: x.imageUrl, fromPostId: x.fromPostId, createdAt: ts?.toMillis?.(), ...(x.self ? { self: true } : {}) }; });
}
